import { env } from '../env.config.js';
import { logger } from '../utils/logger.js';

export class StorageUtils {
  /**
   * Récupère le token JWT depuis le localStorage
   */
  static getToken(): string | null {
    try {
      return localStorage.getItem(env.JWT_STORAGE_KEY) || localStorage.getItem('token');
    } catch (error) {
      logger.error('Erreur lors de la récupération du token:', error);
      return null;
    }
  }

  /**
   * Stocke le token JWT dans le localStorage
   */
  static setToken(token: string): void {
    try {
      localStorage.setItem(env.JWT_STORAGE_KEY, token);
      // Garder aussi l'ancienne clé pour compatibilité
      localStorage.setItem('token', token);
    } catch (error) {
      logger.error('Erreur lors du stockage du token:', error);
    }
  }

  /**
   * Supprime le token JWT du localStorage
   */
  static removeToken(): void {
    try {
      localStorage.removeItem(env.JWT_STORAGE_KEY);
      localStorage.removeItem('token');
    } catch (error) {
      logger.error('Erreur lors de la suppression du token:', error);
    }
  }

  /**
   * Récupère les données utilisateur depuis le localStorage
   */
  static getUser(): import('../types/api.js').User | null {
    try {
      const userData =
        localStorage.getItem(env.USER_STORAGE_KEY) ||
        localStorage.getItem('currentUser') ||
        localStorage.getItem('user');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      logger.error('Erreur lors de la récupération des données utilisateur:', error);
      return null;
    }
  }

  /**
   * Stocke les données utilisateur dans le localStorage
   */
  static setUser(user: import('../types/api.js').User): void {
    try {
      const userString = JSON.stringify(user);
      localStorage.setItem(env.USER_STORAGE_KEY, userString);
      // Garder aussi l'ancienne clé pour compatibilité
      localStorage.setItem('currentUser', userString);
      localStorage.setItem('user', userString);
    } catch (error) {
      logger.error('Erreur lors du stockage des données utilisateur:', error);
    }
  }

  /**
   * Supprime les données utilisateur du localStorage
   */
  static removeUser(): void {
    try {
      localStorage.removeItem(env.USER_STORAGE_KEY);
      localStorage.removeItem('currentUser');
      localStorage.removeItem('user');
    } catch (error) {
      logger.error('Erreur lors de la suppression des données utilisateur:', error);
    }
  }

  /**
   * Récupère le refresh token depuis le localStorage
   */
  static getRefreshToken(): string | null {
    try {
      return localStorage.getItem(env.REFRESH_TOKEN_STORAGE_KEY) || localStorage.getItem('refreshToken');
    } catch (error) {
      logger.error('Erreur lors de la récupération du refresh token:', error);
      return null;
    }
  }

  /**
   * Stocke le refresh token dans le localStorage
   */
  static setRefreshToken(token: string): void {
    try {
      localStorage.setItem(env.REFRESH_TOKEN_STORAGE_KEY, token);
      // Garder aussi l'ancienne clé pour compatibilité
      localStorage.setItem('refreshToken', token);
    } catch (error) {
      logger.error('Erreur lors du stockage du refresh token:', error);
    }
  }

  /**
   * Supprime le refresh token du localStorage
   */
  static removeRefreshToken(): void {
    try {
      localStorage.removeItem(env.REFRESH_TOKEN_STORAGE_KEY);
      localStorage.removeItem('refreshToken');
    } catch (error) {
      logger.error('Erreur lors de la suppression du refresh token:', error);
    }
  }

  /**
   * Récupère le token CSRF de secours
   */
  static getCsrfToken(): string | null {
    try {
      return sessionStorage.getItem('ekose_csrf_token');
    } catch (error) {
      logger.error('Erreur lors de la récupération du token CSRF:', error);
      return null;
    }
  }

  /**
   * Stocke le token CSRF de secours
   */
  static setCsrfToken(token: string): void {
    try {
      sessionStorage.setItem('ekose_csrf_token', token);
    } catch (error) {
      logger.error('Erreur lors du stockage du token CSRF:', error);
    }
  }

  /**
   * Supprime le token CSRF de secours
   */
  static removeCsrfToken(): void {
    try {
      sessionStorage.removeItem('ekose_csrf_token');
    } catch (error) {
      logger.error('Erreur lors de la suppression du token CSRF:', error);
    }
  }

  /**
   * Nettoie complètement le localStorage (déconnexion)
   */
  static clearAll(): void {
    try {
      this.removeToken();
      this.removeUser();
      this.removeRefreshToken();
      this.removeCsrfToken();
      // Nettoyer d'autres clés spécifiques à l'app si nécessaire
      const keysToRemove = [
        'ekose_session',
        'ekose_preferences',
        'ekose_cache',
        'loginAttempts',
        'lockoutUntil'
      ];

      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });
    } catch (error) {
      logger.error('Erreur lors du nettoyage du localStorage:', error);
    }
  }
}
